/**
 * Step 3 (IN): the incident, prefilled from the legacy ticket, then Import.
 *
 * Subject and description are the legacy ones; urgency follows the legacy
 * priority; Service/Category and the customer were settled in the Summary
 * step. Import goes through `importLegacyTicket`, which files it exactly like
 * `createTicket` with `on_behalf_of_email` (Source "IT Helpdesk App").
 */

import {
  Button,
  DescriptionList,
  Field,
  Form,
  Input,
  Radio,
  RadioGroup,
  Textarea,
} from "@norican/react";
import { useState } from "react";

import {
  ErrorState,
  FieldHighlightLegend,
  HighlightedField,
  SectionCard,
} from "@/components/common";
import { useLanguage, useT } from "@/i18n";
import type { LegacySummary, MigrateImportRequest, MigrateImportResponse } from "@/lib/api";
import { prefillHighlight, useFieldHighlight } from "@/lib/field-highlight";

import { useImportLegacyTicket } from "../api";
import {
  buildImportBody,
  buildIncidentDraft,
  type Classification,
  type Customer,
  DESCRIPTION_MAX_LENGTH,
  SUBJECT_MAX_LENGTH,
  SUBJECT_MIN_LENGTH,
  type UrgencyCode,
} from "../lib/migrate-helpers";
import { AttachmentsSection } from "./attachments-section";

const URGENCIES: readonly UrgencyCode[] = ["HIGH", "MEDIUM", "LOW"];

export interface ReviewIncidentProps {
  summary: LegacySummary;
  classification: Classification;
  customer: Customer;
  onBack: () => void;
  onDone: (result: MigrateImportResponse) => void;
}

export function ReviewIncident({
  summary,
  classification,
  customer,
  onBack,
  onDone,
}: ReviewIncidentProps) {
  const { t } = useT("tickets");
  const { language } = useLanguage();
  // What the legacy ticket filled in, kept for the field-state tint (7 Oct).
  const [legacy] = useState(() => buildIncidentDraft(summary));
  const [draft, setDraft] = useState(legacy);
  const subjectState = useFieldHighlight(legacy.subject, draft.subject, true);
  const descriptionState = useFieldHighlight(legacy.description, draft.description, true);
  const urgencyState = prefillHighlight(legacy.urgency, draft.urgency);
  const [attachmentIds, setAttachmentIds] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const importTicket = useImportLegacyTicket();

  const subjectOk = draft.subject.trim().length >= SUBJECT_MIN_LENGTH;
  const descriptionOk = draft.description.trim().length > 0;
  const busy = importTicket.isPending || uploading;

  const handleImport = () => {
    if (!subjectOk || !descriptionOk || busy) {
      return;
    }
    const body = buildImportBody({
      legacyNumber: summary.legacy_number,
      classification,
      customer,
      subject: draft.subject,
      description: draft.description,
      language,
      urgency: draft.urgency,
      attachmentIds,
    }) as MigrateImportRequest;
    importTicket.mutate({ body, idempotencyKey }, { onSuccess: onDone });
  };

  return (
    <Form className="flex flex-col gap-6" onFormSubmit={handleImport}>
      <SectionCard title={t("migrate.review.titleIN")}>
        <div className="flex flex-col gap-4">
          <DescriptionList
            orientation="horizontal"
            items={[
              {
                label: t("migrate.summary.customer"),
                value: `${customer.displayName} (${customer.email})`,
              },
              { label: t("migrate.summary.service"), value: classification.service ?? "" },
              { label: t("migrate.summary.category"), value: classification.category ?? "" },
            ]}
          />
          <FieldHighlightLegend />
          <HighlightedField state={subjectState}>
            <Field
              name="subject"
              label={t("migrate.review.subject")}
              error={subjectOk ? undefined : t("migrate.review.subjectRequired")}
              required
            >
              <Input
                name="subject"
                value={draft.subject}
                maxLength={SUBJECT_MAX_LENGTH}
                onChange={(event) => setDraft({ ...draft, subject: event.target.value })}
              />
            </Field>
          </HighlightedField>
          <HighlightedField state={descriptionState}>
            <Field
              name="description"
              label={t("migrate.review.description")}
              error={descriptionOk ? undefined : t("migrate.review.descriptionRequired")}
              required
            >
              <Textarea
                name="description"
                value={draft.description}
                maxLength={DESCRIPTION_MAX_LENGTH}
                rows={8}
                autoResize
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
              />
            </Field>
          </HighlightedField>
          <HighlightedField state={urgencyState}>
            <Field name="urgency" label={t("migrate.review.urgency")}>
              <RadioGroup
                value={draft.urgency}
                onValueChange={(value) => setDraft({ ...draft, urgency: value as UrgencyCode })}
                className="flex flex-wrap gap-4"
              >
                {URGENCIES.map((level) => (
                  <Radio.Root key={level} value={level} label={t(`level.${level}`)} />
                ))}
              </RadioGroup>
            </Field>
          </HighlightedField>
        </div>
      </SectionCard>

      <AttachmentsSection
        summary={summary}
        onAttachmentIdsChange={setAttachmentIds}
        onPendingChange={setUploading}
        disabled={importTicket.isPending}
      />

      {importTicket.isError ? <ErrorState variant="inline" error={importTicket.error} /> : null}

      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button type="button" variant="ghost" tone="neutral" onClick={onBack} disabled={busy}>
          {t("migrate.back")}
        </Button>
        <Button type="submit" loading={busy} disabled={busy || !subjectOk || !descriptionOk}>
          {importTicket.isPending ? t("migrate.review.importing") : t("migrate.review.import")}
        </Button>
      </div>
    </Form>
  );
}
